import { z } from 'zod';
import { categoryPermissionViolations, isWritePermission } from './permissions';
import {
  MAX_SCHOOL_RANK,
  categoryForRank,
  roleCategory,
  roleCategoryOf,
  type RoleCategoryId,
} from './role-categories';

/**
 * Role rules that look at the whole school (decision 2026-10-10 #5): unique ranks, ranks inside
 * the role's category, and the roles report a Tenant Admin sees when a role breaks a rule
 * (typically one kept from before role categories: the migration flags, never rewrites).
 */

export interface RankedRole {
  id: string;
  name: string;
  rank: number;
  category?: string | null;
  permissions: readonly string[];
}

/** Ranks already used by other roles (pass the role being edited as `exceptRoleId`). */
export function takenRanks(
  roles: readonly Pick<RankedRole, 'id' | 'rank'>[],
  exceptRoleId?: string,
): number[] {
  return [...new Set(roles.filter((r) => r.id !== exceptRoleId).map((r) => r.rank))].sort(
    (a, b) => a - b,
  );
}

/** The role that already uses this rank (other than `exceptRoleId`), if any. */
export function rankHolder<T extends Pick<RankedRole, 'id' | 'rank'>>(
  roles: readonly T[],
  rank: number,
  exceptRoleId?: string,
): T | null {
  return roles.find((r) => r.rank === rank && r.id !== exceptRoleId) ?? null;
}

/** Free ranks inside a category's range, lowest first. */
export function freeRanksInCategory(
  roles: readonly Pick<RankedRole, 'id' | 'rank'>[],
  category: RoleCategoryId,
  exceptRoleId?: string,
): number[] {
  const { minRank, maxRank } = roleCategory(category);
  const taken = new Set(takenRanks(roles, exceptRoleId));
  return Array.from({ length: maxRank - minRank + 1 }, (_, i) => minRank + i).filter(
    (r) => !taken.has(r),
  );
}

export const ROLE_ISSUE_CODES = [
  'DUPLICATE_RANK',
  'RANK_RESERVED',
  'RANK_OUTSIDE_CATEGORY',
  'USERS_CATEGORY_WRITE',
  'PERMISSION_NOT_ALLOWED_FOR_CATEGORY',
] as const;
export type RoleIssueCode = (typeof ROLE_ISSUE_CODES)[number];

export const roleIssueSchema = z.object({
  roleId: z.string().min(1),
  roleName: z.string(),
  code: z.enum(ROLE_ISSUE_CODES),
  message: z.string().min(1),
  /** DUPLICATE_RANK: the other roles on the same rank. */
  conflictsWith: z.array(z.string()).optional(),
  /** *_WRITE / *_NOT_ALLOWED_*: the permissions to remove. */
  permissions: z.array(z.string()).optional(),
});
export type RoleIssue = z.infer<typeof roleIssueSchema>;

function rankIssues(role: RankedRole, roles: readonly RankedRole[]): RoleIssue[] {
  const base = { roleId: role.id, roleName: role.name };
  const category = roleCategoryOf(role);
  const others = roles.filter((r) => r.id !== role.id && r.rank === role.rank);
  const issues: RoleIssue[] = [];
  if (others.length > 0) {
    issues.push({
      ...base,
      code: 'DUPLICATE_RANK',
      message: `Rank ${role.rank} is also used by ${others.map((r) => r.name).join(', ')}. Give each role its own rank.`,
      conflictsWith: others.map((r) => r.id),
    });
  }
  if (role.rank > MAX_SCHOOL_RANK) {
    issues.push({
      ...base,
      code: 'RANK_RESERVED',
      message: `Rank ${role.rank} is reserved for TeachPartner. Choose a rank of ${MAX_SCHOOL_RANK} or lower.`,
    });
  } else if (categoryForRank(role.rank) !== category) {
    const { title, minRank, maxRank } = roleCategory(category);
    issues.push({
      ...base,
      code: 'RANK_OUTSIDE_CATEGORY',
      message: `Rank ${role.rank} is outside ${title} (${minRank}–${maxRank}).`,
    });
  }
  return issues;
}

function permissionIssues(role: RankedRole): RoleIssue[] {
  const base = { roleId: role.id, roleName: role.name };
  const category = roleCategoryOf(role);
  const writes = category === 'users' ? role.permissions.filter(isWritePermission) : [];
  const gated = categoryPermissionViolations(category, role.permissions).filter(
    (p) => !writes.includes(p),
  );
  const title = roleCategory(category).title;
  return [
    ...(writes.length > 0
      ? [
          {
            ...base,
            code: 'USERS_CATEGORY_WRITE' as const,
            message: `Roles in ${title} can't change anything. Remove: ${writes.join(', ')}.`,
            permissions: writes,
          },
        ]
      : []),
    ...(gated.length > 0
      ? [
          {
            ...base,
            code: 'PERMISSION_NOT_ALLOWED_FOR_CATEGORY' as const,
            message: `${title} roles can't hold: ${gated.join(', ')}.`,
            permissions: gated,
          },
        ]
      : []),
  ];
}

/**
 * Every rule a school's roles currently break, one entry per role and problem. Empty for a
 * school that follows the rules. tenant_admin always holds every permission and is exempt
 * from the permission checks.
 */
export function roleIssues(roles: readonly RankedRole[]): RoleIssue[] {
  return roles.flatMap((role) => [
    ...rankIssues(role, roles),
    ...(role.id === 'tenant_admin' ? [] : permissionIssues(role)),
  ]);
}
